import { message } from "antd";
import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from "axios";
import qs from "qs";

import { clearToken, getToken } from "@/utils/storage";

/** 与后端约定的统一响应结构，可按实际接口调整字段名 */
export interface ApiResponse<T = unknown> {
  code: number;
  data: T;
  message: string;
}

/** 判断响应体是否符合约定的业务包结构 */
function isApiResponse(payload: unknown): payload is ApiResponse {
  return (
    typeof payload === "object" &&
    payload !== null &&
    "code" in payload &&
    typeof (payload as ApiResponse).code === "number"
  );
}

/** 在非组件环境（axios 拦截器）中登出：使用 router 实例导航 */
function loginOut(redirectUrl?: string) {
  clearToken();
  message.error("登录过期，请重新登录");
  // 如果redirectUrl不包含redirectUrl，则添加redirectUrl参数并跳转登录页
  if (redirectUrl && !redirectUrl.includes("redirectUrl")) {
    const search = `?redirectUrl=${encodeURIComponent(redirectUrl)}`;
    window.location.href = `/login${search}`;
  } else {
    window.location.href = `/login`;
  }
}

/** 从 Axios 错误或通用 Error 中解析可读错误文案 */
function readHttpErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { message?: string } | undefined;
    if (data?.message) return String(data.message);
    if (error.message) return error.message;
  }
  if (error instanceof Error) return error.message;
  return "网络连接异常";
}

/** Spring query 序列化选项：点号嵌套 + 跳过空值 */
const SPRING_QUERY_OPTIONS: qs.IStringifyOptions = {
  allowDots: true,
  skipNulls: true,
  arrayFormat: "indices",
  filter: (_prefix, value) => {
    // 空字符串不参与 query，避免干扰后端默认行为
    if (value === "" || value === undefined || value === null) return;
    return value;
  },
};

/**
 * Spring / Java 后端 GET query 序列化
 * 例：{ arg0: { pageNum: 1 } } → arg0.pageNum=1
 * @param params query 对象
 */
export function springQuerySerializer(params: Record<string, unknown>): string {
  return qs.stringify(params, SPRING_QUERY_OPTIONS);
}

const httpClient: AxiosInstance = axios.create({
  // 本地可与 `.env` 中 `VITE_*` 配合；若为空则依赖相对路径 + Vite `/api` 代理转发后端
  baseURL: "",
  timeout: 60_000,
  headers: {
    "Content-Type": "application/json",
  },
  // 全局 Spring 风格 query 序列化，扁平 / 嵌套 params 均适用
  paramsSerializer: springQuerySerializer,
});

httpClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getToken();
    if (token) {
      config.headers.token = token;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

httpClient.interceptors.response.use(
  (response: AxiosResponse<unknown>) => {
    const payload = response.data;
    // 非业务 JSON（blob / text 等）保持 Axios 标准结构，不做 code 校验
    if (!isApiResponse(payload)) {
      return response;
    }
    // 与后端约定：code === 0 表示业务成功（若后端用 200，请改此处）
    if (payload.code === 0) {
      return response;
    }
    const pathname = window.location.pathname;
    // 返回401XX表示token失效，则清除token，并跳转登录页
    if (payload.code.toString().includes("401") && pathname && !pathname.includes("login")) {
      loginOut(pathname);
    }
    message.error(payload.message || "请求失败");
    return Promise.reject(new Error(payload.message || "Error"));
  },
  (error) => {
    message.error(readHttpErrorMessage(error));
    return Promise.reject(error);
  },
);

/**
 * 从已通过拦截器校验的 JSON 响应中取出业务包
 * @param res Axios 原始响应，data 字段为 ApiResponse
 */
function pickApiResponse<T>(res: AxiosResponse<unknown>): ApiResponse<T> {
  return res.data as ApiResponse<T>;
}

/**
 * GET JSON 接口（query 走全局 Spring 序列化，嵌套 params 如 { arg0: query }）
 * @param url 请求地址
 * @param config 可选 axios 配置（如 params）
 * @returns 统一业务包；T 为 data 字段类型
 */
async function get<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
  const res = await httpClient.get(url, config);
  return pickApiResponse<T>(res);
}

/**
 * GET 下载文件
 * @param url 请求地址
 * @param config 可选 axios 配置
 */
async function getBlob(url: string, config?: AxiosRequestConfig): Promise<Blob> {
  const res = await httpClient.get<Blob>(url, {
    ...config,
    responseType: "blob",
  });
  return res.data;
}

/**
 * GET 下载二进制
 * @param url 请求地址
 * @param config 可选 axios 配置
 */
async function getArrayBuffer(url: string, config?: AxiosRequestConfig): Promise<ArrayBuffer> {
  const res = await httpClient.get<ArrayBuffer>(url, {
    ...config,
    responseType: "arraybuffer",
  });
  return res.data;
}

/**
 * GET 纯文本响应
 * @param url 请求地址
 * @param config 可选 axios 配置
 */
async function getText(url: string, config?: AxiosRequestConfig): Promise<string> {
  const res = await httpClient.get<string>(url, {
    ...config,
    responseType: "text",
  });
  return res.data;
}

/**
 * POST 请求（JSON body）
 * @param url 请求地址
 * @param data 请求体
 * @param config 可选 axios 配置
 */
async function post<T = unknown>(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig,
): Promise<ApiResponse<T>> {
  const res = await httpClient.post(url, data, config);
  return pickApiResponse<T>(res);
}

/**
 * PUT 请求（按后端语义创建或全量更新资源）
 * @param url 请求地址
 * @param data 请求体
 * @param config 可选 axios 配置
 */
async function put<T = unknown>(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig,
): Promise<ApiResponse<T>> {
  const res = await httpClient.put(url, data, config);
  return pickApiResponse<T>(res);
}

/**
 * DELETE 请求；`delete` 为保留字故单独封装，对外挂在 `http.delete`
 * @param url 请求地址
 * @param config 可含 params；需在 body 中传参时使用 `config.data`
 */
async function deleteMethod<T = unknown>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<ApiResponse<T>> {
  const res = await httpClient.delete(url, config);
  return pickApiResponse<T>(res);
}

/** 对外 HTTP 方法集合，已走统一请求/响应拦截器 */
export const http = {
  get,
  getBlob,
  getArrayBuffer,
  getText,
  post,
  put,
  delete: deleteMethod,
};

export { httpClient };

export default http;
